import { z } from "zod";

export const CUSTOM_SKILL_STATUS_VALUES = ["ACTIVE", "DISABLED"] as const;
export const CustomSkillStatusEnum = z.enum(CUSTOM_SKILL_STATUS_VALUES);
export type CustomSkillStatus = z.infer<typeof CustomSkillStatusEnum>;
export const CUSTOM_SKILL_STATUS = CustomSkillStatusEnum.enum;

export const CustomSkillToolSchema = z.object({
  id: z.string(),
  actionId: z.string(),
  connectionId: z.string().nullable(),
});

export type CustomSkillTool = z.infer<typeof CustomSkillToolSchema>;

export const CustomSkillSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  name: z.string(),
  description: z.string(),
  instructions: z.string(),
  status: CustomSkillStatusEnum,
  tools: z.array(CustomSkillToolSchema),
  /** Git SHA of the last sync; non-null means the skill is covered by code sync. */
  syncVersion: z.string().nullable(),
  /** "sync" if last written by the sync, else the user id who edited it. */
  syncUpdatedBy: z.string().nullable(),
  createdByUserId: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type CustomSkill = z.infer<typeof CustomSkillSchema>;

/** Only alphanumeric characters, hyphens, and underscores — prevents path traversal in sandbox skill paths. */
const safeSkillName = z
  .string()
  .min(1)
  .max(100)
  .regex(
    /^[a-zA-Z0-9_-]+$/,
    "Skill name must only contain letters, numbers, hyphens, and underscores",
  );

export const CreateCustomSkillParamsSchema = z.object({
  name: safeSkillName,
  description: z.string().min(1).max(500),
  instructions: z.string().min(1),
  tools: z.array(
    z.object({
      actionId: z.string().min(1),
      connectionId: z.string().nullable().optional(),
    }),
  ),
});

export type CreateCustomSkillParams = z.infer<
  typeof CreateCustomSkillParamsSchema
>;

export const UpdateCustomSkillParamsSchema = z.object({
  name: safeSkillName.optional(),
  description: z.string().min(1).max(500).optional(),
  instructions: z.string().min(1).optional(),
  status: CustomSkillStatusEnum.optional(),
  tools: z
    .array(
      z.object({
        actionId: z.string().min(1),
        connectionId: z.string().nullable().optional(),
      }),
    )
    .optional(),
});

export type UpdateCustomSkillParams = z.infer<
  typeof UpdateCustomSkillParamsSchema
>;

/** Code-authored skill — source of truth for eval seeding + DB sync. v1: text + status only; tools deferred (FP-987). */
export const CustomSkillDefinitionSchema = CreateCustomSkillParamsSchema.omit({
  tools: true,
}).extend({
  status: CustomSkillStatusEnum.optional(),
});

export type CustomSkillDefinition = z.infer<typeof CustomSkillDefinitionSchema>;

/** Maps a definition to create params; v1 emits no tools. */
export function customSkillDefinitionToCreateParams(
  definition: CustomSkillDefinition,
): CreateCustomSkillParams {
  return {
    name: definition.name,
    description: definition.description,
    instructions: definition.instructions,
    tools: [],
  };
}

// API Response schemas

export const CustomSkillResponseSchema = z.object({
  customSkill: CustomSkillSchema,
});

export type CustomSkillResponse = z.infer<typeof CustomSkillResponseSchema>;

export const ListCustomSkillsResponseSchema = z.object({
  customSkills: z.array(CustomSkillSchema),
});

export type ListCustomSkillsResponse = z.infer<
  typeof ListCustomSkillsResponseSchema
>;

export const DeleteCustomSkillResponseSchema = z.object({
  success: z.boolean(),
});

export type DeleteCustomSkillResponse = z.infer<
  typeof DeleteCustomSkillResponseSchema
>;

export const CustomSkillIdParamsSchema = z.object({
  id: z.string(),
});

export type CustomSkillIdParams = z.infer<typeof CustomSkillIdParamsSchema>;
